'use client';

import BaseText from '@dvd/components/BaseText';
import { styled } from 'styled-components';
import Media from '@dvd/components/Media';
import type { ImageProps } from '@types';
import type { ReactNode } from 'react';

export type FeatureSplitChildProps = {
  /**
   * The hashed id of the video; nesc. for showing video
   */
  hashedId?: string;
  /**
   * The featured image of the feature split
   */
  featuredImage?: ImageProps;
  /**
   * The headline of the feature split
   */
  headline: string;
  /**
   * The description of the feature split
   * - Use a simple string (e.g. "A sentence of words.") for straight copy.
   * - Use HTML to apply simple formatting (e.g. A sentence with an <strong>important</strong> word.)
   */
  description: ReactNode;
};

export const FeatureSplitMedia = styled(Media)`
  grid-column: 1 / span 2;
  margin-block-end: ${({ theme }) => theme.spacing[1]};
  margin-block-start: 0;

  ${({ theme }) => theme.mq.minWidth.medium} {
    margin-block: 0;
  }
`;

const FeatureSplitBaseText = styled(BaseText)`
  p {
    font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
  }

  h4 {
    margin-block-end: ${({ theme }) => theme.spacing[0.5]};
  }

  padding-block-end: ${({ theme }) => theme.spacing[0.5]};

  ${({ theme }) => theme.mq.minWidth.medium} {
    p {
      font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    p {
      font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
    }
  }
`;

/**
 *
 * The layout of FeatureSplitChild is largely styled in its parent module, FeatureSplit.
 * This component is simply to demonstrate how props are used.
 *
 * Rules:
 * - Only to be used in FeatureSplit module
 * - headline and description are required
 * - hashedId is required for video content
 * - imageSrc, imageAlt, imageHeight, and imageWidth are required for image content
 * - if hashedId is provided, imageSrc, imageAlt, imageHeight, and imageWidth are ignored
 * - if imageSrc is provided, hashedId is ignored
 */
const FeatureSplitChild = ({
  hashedId,
  featuredImage,
  headline,
  description,
}: FeatureSplitChildProps) => {
  return (
    <>
      {hashedId ? (
        <FeatureSplitMedia hashedId={hashedId} />
      ) : (
        <FeatureSplitMedia
          alt={featuredImage.alt}
          height={featuredImage.height}
          src={featuredImage.src}
          width={featuredImage.width}
        />
      )}
      <FeatureSplitBaseText
        className="FeatureSplit__base-text"
        headingLevel="h4"
        heading={headline}
        body={description}
      />
    </>
  );
};

export default FeatureSplitChild;
